import rest from 'Services/restFootData';
import qs from 'query-string';
import { SubmissionError } from 'redux-form';
import { getLinkId } from 'Services/tools';

export function fetchTournament(id) {
  return function(dispatch) {
    dispatch({ type: 'FETCH_TOURNAMENT' });
    // _image loads the per-season logo so the detail form can display and edit it (V2-2069),
    // mirroring how competitions/clubs/channels fetch their image. _competition brings the parent
    // competition so the breadcrumb can show its CURRENT name: the flat competitionName is now the
    // season's historical snapshot, which would mislabel a crumb that links to the competition.
    return rest
      .get(`tournaments/${id}`, { params: { groups: ['read', '_competition', '_image'] } })
      .then(
        res => dispatch({ type: 'FETCH_TOURNAMENT_FULFILLED', payload: res.data }),
        err => dispatch({ type: 'FETCH_TOURNAMENT_REJECTED', payload: err.response })
      );
  };
}

export function fetchTournaments(search) {
  const params = {
    itemsPerPage: 10,
    partial: 0,
    ...qs.parse(search),
    groups: ['read'],
    'order[competition.translations.priority]': 'asc',
  };

  return function(dispatch) {
    dispatch({ type: 'FETCH_TOURNAMENTS' });
    return rest.get('tournaments', { params }).then(
      res => dispatch({ type: 'FETCH_TOURNAMENTS_FULFILLED', payload: res.data }),
      err =>
        dispatch({
          type: 'FETCH_TOURNAMENTS_REJECTED',
          payload: err.response,
        })
    );
  };
}

export function fetchCompetitionTournaments(competition) {
  const params = {
    competition,
    pagination: 0,
  };

  return function(dispatch) {
    dispatch({ type: 'FETCH_COMPETITION_TOURNAMENTS', payload: competition });

    return rest.get('tournaments', { params }).then(
      res =>
        dispatch({
          type: 'FETCH_COMPETITION_TOURNAMENTS_FULFILLED',
          payload: res.data,
        }),
      err =>
        dispatch({
          type: 'FETCH_COMPETITION_TOURNAMENTS_REJECTED',
          payload: err.response,
        })
    );
  };
}

export function updateTournament(id, tournament) {
  return function(dispatch) {
    dispatch({ type: 'UPDATE_TOURNAMENT', payload: id });
    return rest.put(`tournaments/${id}`, tournament).then(
      res => {
        dispatch({
          type: 'ADD_NOTIFICATION',
          payload: { messageKey: 'message_tournamentUpdated', type: 'success' },
        });
        dispatch({ type: 'UPDATE_TOURNAMENT_FULFILLED', payload: res.data });
        // Refresh with read + _image so the form (incl. the per-season logo) reflects the persisted
        // state: the PUT response carries the write group's read side only (no _image), and passing
        // groups on the PUT would override the denormalization (write) context. The save already
        // succeeded, so this refresh is best-effort — on failure, leave the form as the user left it
        // (return without a payload so FormEdit skips re-initialization) and warn them to reload to
        // confirm the change stuck. A failed refresh never runs on a failed update (that's the err path).
        const refreshFailed = () => {
          dispatch({
            type: 'ADD_NOTIFICATION',
            payload: { messageKey: 'message_tournamentUpdatedButRefreshFailed', type: 'warn' },
          });
          return {};
        };
        return dispatch(fetchTournament(id)).then(
          refetch => (refetch && refetch.type === 'FETCH_TOURNAMENT_FULFILLED' ? refetch : refreshFailed()),
          refreshFailed
        );
      },
      err => {
        if (err.response && err.response.data.type === 'ConstraintViolationList') {
          const { errors } = err.response.data;
          throw new SubmissionError(errors);
        }
        return dispatch({
          type: 'UPDATE_TOURNAMENT_REJECTED',
          payload: err.response,
        });
      }
    );
  };
}

const activeTournamentIdByCompetition = {};

/**
 * Resolve the active tournament (current season) of a competition. Memoized per competition
 * for the page lifetime; resolves to null when the competition has no active tournament.
 * `competition` may be an IRI or a raw id.
 */
export function getActiveTournamentId(competition) {
  const competitionId = getLinkId(competition);
  if (!competitionId) {
    return Promise.resolve(null);
  }
  if (!activeTournamentIdByCompetition[competitionId]) {
    activeTournamentIdByCompetition[competitionId] = rest
      .get('tournaments', {
        params: { competition: competitionId, isActive: 1, groups: ['read_link'], itemsPerPage: 1 },
      })
      .then(
        res => (res.data && res.data.collection && res.data.collection[0] ? res.data.collection[0].id : null),
        () => {
          delete activeTournamentIdByCompetition[competitionId];
          return null;
        }
      );
  }

  return activeTournamentIdByCompetition[competitionId];
}
